"use client";

import { useMemo, type CSSProperties, type ReactNode } from "react";
import type { DocTheme } from "@/lib/theme";
import { themeToCssVars } from "@/lib/theme";

interface ThemeScopeProps {
  /** Effective theme (already resolved per-doc ?? user-default ?? default). */
  theme: DocTheme;
  children: ReactNode;
  className?: string;
}

/**
 * Applies a document theme as scoped CSS variables on a wrapper element. The
 * computed vars (`--text-h1`, `--font-heading`, `--doc-space`, …) override the
 * inherited design-system tokens for everything inside, and are consumed by
 * `@workspace/markdown/doc-typography.css`. Nothing leaks to the app chrome.
 *
 * This element also PAINTS the theme's surface. `--background` has to be
 * applied here rather than on `.docs-editor`: in the editor this wrapper is the
 * scroll container, so painting the inner content element would colour only the
 * text block and leave the surrounding scroll area on the app's global
 * background — the themed page would end partway down the screen.
 *
 * Used to wrap both the live TipTap editor and the read/print view.
 */
export function ThemeScope({ theme, children, className }: ThemeScopeProps) {
  const style = useMemo(
    () => themeToCssVars(theme) as CSSProperties,
    [theme],
  );

  return (
    <div
      className={className}
      style={{ ...style, background: "var(--doc-background)" }}
    >
      {children}
    </div>
  );
}
